import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { renderReportPreview } from '../../library/fr3render';
import { buildPreviewInput, fetchRunDatasets } from './reportPipeline';

/**
 * Inline PDF view of a finished report run (the default result view for a
 * report with an .fr3 layout, like MyFashionManager's print preview). When
 * nothing can be rendered (.frf only, layout missing, render error) it calls
 * onFallback(message) so the caller switches to the grid.
 */
const ReportPdfView = (props) => {
    const t = props.t || ((k) => k);
    const tx = (key, fallback) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };
    const [phase, setPhase] = useState('loading'); // loading | done | error
    const [pdfUrl, setPdfUrl] = useState('');
    const [warnings, setWarnings] = useState([]);
    const [message, setMessage] = useState('');
    const cancelled = useRef(false);

    useEffect(() => {
        cancelled.current = false;
        let url = '';
        (async () => {
            try {
                setPhase('loading');
                const status = await fetchRunDatasets({ api: props.api, runId: props.runId });
                if (cancelled.current) return;
                const built = await buildPreviewInput({
                    api: props.api,
                    status,
                    documentKind: props.documentKind || 'ticket',
                    storeId: props.storeId || '',
                    posId: props.posId || '',
                    title: props.title || '',
                    lang: props.lang || '',
                    variables: props.variables || null,
                });
                if (cancelled.current) return;
                const result = await renderReportPreview(built);
                if (cancelled.current) return;
                url = URL.createObjectURL(new Blob([result.pdf], { type: 'application/pdf' }));
                setPdfUrl(url);
                setWarnings(result.warnings || []);
                setPhase('done');
            } catch (error) {
                if (cancelled.current) return;
                const msg = error?.message || 'Render failed';
                setMessage(msg);
                setPhase('error');
                if (props.onFallback) props.onFallback(msg);
            }
        })();
        return () => {
            cancelled.current = true;
            if (url) URL.revokeObjectURL(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.runId]);

    if (phase === 'loading') {
        return (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">{tx('rpv_rendering', 'Rendering the layout…')}</Typography>
            </Stack>
        );
    }
    if (phase === 'error') {
        return <Alert severity="warning">{message}</Alert>;
    }
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
            {warnings.length > 0 && (
                <Alert severity="info" sx={{ py: 0 }}>{warnings.join(' · ')}</Alert>
            )}
            <Box sx={{ flex: 1, minHeight: '60vh', border: '1px solid rgba(0,0,0,0.12)' }}>
                <iframe title="report-pdf" src={pdfUrl} style={{ width: '100%', height: '100%', border: 0 }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" component="a" href={pdfUrl} download={(props.title || 'report') + '.pdf'}>
                    {tx('rpv_download', 'Download PDF')}
                </Button>
            </Box>
        </Box>
    );
};

export default ReportPdfView;
